import { useState, useEffect } from 'react';

// Full-bleed hero backdrop. Uses 8K renders so object-cover can fill the whole
// banner at ANY screen size WITHOUT upscale blur (an 8K source always downscales
// to fit, never upscales). Add more 8K renders here (each a WebP under
// /feature-renders/wide-screen/8k/) and they'll crossfade.
const HERO_BACKGROUNDS = [
  '/feature-renders/wide-screen/8k/radiance-vitamin-c-serum-8k.webp', // vitamin C serum
  '/feature-renders/wide-screen/8k/radiance-brightening-serum-8k.webp', // brightening serum
  '/feature-renders/wide-screen/8k/radiance-brightening-serum-studio-8k.webp', // brightening serum (studio)
  '/feature-renders/wide-screen/8k/hydrate-daily-moisturizer-8k.webp', // daily moisturizer
];

export default function Hero() {
  const [bgIndex, setBgIndex] = useState(0);

  // Crossfade the backdrop every 5s.
  useEffect(() => {
    const id = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % HERO_BACKGROUNDS.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  // The crossfading image stack — inlined in both the desktop full-bleed
  // backdrop and the mobile top image block. Same 4 src URLs in both, so the
  // browser only fetches 4 unique images regardless of which layout renders.
  const crossfade = HERO_BACKGROUNDS.map((src, i) => (
    <img
      key={src}
      src={src}
      alt=""
      aria-hidden="true"
      className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${
        i === bgIndex ? 'opacity-100' : 'opacity-0'
      }`}
    />
  ));

  return (
    <section className="relative w-full overflow-hidden bg-white">
      {/* ============================ DESKTOP (lg+) ============================
          Full-bleed backdrop with the headline overlaid on the left. Unchanged
          from the original design (desktop was never the problem). */}
      <div className="relative hidden lg:block min-h-screen">
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {crossfade}
        </div>

        {/* Left-side scrim — darkens the text side, fades to clear before the
            product so the white headline/subtitle stay readable over bright images. */}
        <div
          className="absolute inset-0 z-[5] bg-gradient-to-r from-black/75 via-black/45 to-transparent pointer-events-none"
          aria-hidden="true"
        />

        <div className="relative z-10 flex items-center min-h-screen px-16 pt-24 pb-12">
          <div className="w-[52%] text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-surface-dark bg-surface mb-8">
              <i className="ri-rocket-2-line text-primary text-sm" />
              <span className="text-primary text-sm font-medium tracking-wide">
                The AI Ad Engine for Ecommerce
              </span>
            </div>

            {/* Main Title */}
            <h1 className="font-display text-6xl font-bold text-white leading-[1.05] tracking-tight mb-6">
              Turn your store into
              <br />
              <span className="text-white">high ROAS ads</span>
              {' '}
              on autopilot.
            </h1>

            {/* Subtitle */}
            <p className="text-white text-xl max-w-xl mb-6 leading-relaxed">
              We study your competitors, write the winning ad, render it with an
              engine no one else has, and publish it for you. Not just images.
              Your whole ad engine.
            </p>

            {/* Proof line */}
            <p className="inline-flex items-center gap-2 text-white/95 text-base font-semibold mb-9">
              <i className="ri-line-chart-line text-primary" />
              Our customers are seeing up to 4x+ ROAS. No designer, no agency, no big budget.
            </p>

            {/* CTA */}
            <div className="flex flex-wrap items-center gap-4 mb-12">
              <a
                href="/auth/sign-up"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary-hover text-white text-base font-semibold rounded-full transition-all group shadow-lg shadow-primary/15"
              >
                Start Free
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/try"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/90 hover:bg-white text-dark text-base font-semibold transition-all shadow-lg shadow-black/10"
              >
                Try it free
                <i className="ri-magic-line text-primary" />
              </a>
            </div>

            {/* Stats — the engine, not just images */}
            <div className="flex items-center gap-8">
              <div>
                <span className="text-white text-2xl font-display font-bold block">4x+</span>
                <span className="text-white text-sm">Client ROAS</span>
              </div>
              <div className="w-px h-8 bg-dark/10" />
              <div>
                <span className="text-white text-2xl font-display font-bold block">100+</span>
                <span className="text-white text-sm">Ad recipes</span>
              </div>
              <div className="w-px h-8 bg-dark/10" />
              <div>
                <span className="text-white text-2xl font-display font-bold block">1</span>
                <span className="text-white text-sm">Platform, full funnel</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================ MOBILE (<lg) ============================
          Stacked: hero image on top (with a prominent Start-Free-Trial CTA
          overlaid), then the copy on a clean white panel below with DARK,
          readable type + a second full-width Start-Free-Trial. Fixes the
          white-text-over-bright-image readability problem. */}
      <div className="lg:hidden">
        {/* Hero image block */}
        <div className="relative w-full h-[62vh] min-h-[460px] overflow-hidden">
          {crossfade}

          {/* Bottom scrim so the overlaid CTA reads against a bright image. */}
          <div
            className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/50 via-black/20 to-transparent pointer-events-none"
            aria-hidden="true"
          />

          {/* Offer line + CTA — anchored BOTTOM-CENTER on the image. The short
              backfilled line is a risk-reducer for the post-ad landing (echoes
              the trial offer, kills hesitation right next to the button). */}
          <div className="absolute inset-x-0 bottom-6 px-6 flex flex-col items-center gap-3">
            <a
              href="/auth/sign-up"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary-hover text-white text-base font-bold rounded-full transition-all group"
              style={{ boxShadow: '0 6px 18px rgba(0,0,0,0.30), 0 24px 60px rgba(0,0,0,0.38)' }}
            >
              Start Free
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </a>
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-dark text-xs font-semibold shadow-md">
              <i className="ri-sparkling-2-line text-primary" />
              No card required · 20 free credits
            </span>
          </div>
        </div>

        {/* Copy panel — below the image, on white for readability */}
        <div className="px-6 py-10 bg-white text-left">
          {/* Badge — on the white panel (avoids colliding with the sticky nav) */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-surface-dark bg-surface mb-6">
            <i className="ri-rocket-2-line text-primary text-sm" />
            <span className="text-primary text-sm font-medium tracking-wide">
              The AI Ad Engine for Ecommerce
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-dark leading-[1.08] tracking-tight mb-4">
            Turn your store into{' '}
            <span className="text-primary">high ROAS ads</span> on autopilot.
          </h1>

          <p className="text-dark-muted text-base sm:text-lg leading-relaxed mb-5">
            We study your competitors, write the winning ad, render it with an
            engine no one else has, and publish it for you. Not just images.
            Your whole ad engine.
          </p>

          {/* Proof line */}
          <p className="inline-flex items-start gap-2 text-dark text-sm sm:text-base font-semibold mb-7">
            <i className="ri-line-chart-line text-primary mt-0.5" />
            Customers are seeing up to 4x+ ROAS. No designer, no agency, no big budget.
          </p>

          {/* Prominent full-width CTAs on the text panel */}
          <a
            href="/auth/sign-up"
            className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-primary hover:bg-primary-hover text-white text-base font-bold rounded-full shadow-lg shadow-primary/20 transition-all group mb-3"
          >
            Start Free
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="/try"
            className="w-full flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-dark-muted/25 text-dark text-base font-semibold transition-all mb-9"
          >
            Try it free
            <i className="ri-magic-line text-primary" />
          </a>

          {/* Stats */}
          <div className="flex items-center gap-6">
            <div>
              <span className="text-dark text-2xl font-display font-bold block">4x+</span>
              <span className="text-dark-muted text-sm">Client ROAS</span>
            </div>
            <div className="w-px h-8 bg-dark/10" />
            <div>
              <span className="text-dark text-2xl font-display font-bold block">100+</span>
              <span className="text-dark-muted text-sm">Ad recipes</span>
            </div>
            <div className="w-px h-8 bg-dark/10" />
            <div>
              <span className="text-dark text-2xl font-display font-bold block">1</span>
              <span className="text-dark-muted text-sm">Full funnel</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
